import { useEffect, useRef, useState } from "react";

type Props = {
  onChange: (svg: string | null) => void;
  height?: number;
};

/**
 * Hand-drawn signature → SVG path (local + backend stub).
 */
export function SignaturePad({ onChange, height = 180 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const strokes = useRef<Array<Array<{ x: number; y: number }>>>([]);
  const [hasInk, setHasInk] = useState(false);

  const syncSize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1b3a4b";
    ctx.lineWidth = 2.4;
    redraw();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, height);
    for (const stroke of strokes.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0]!.x, stroke[0]!.y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i]!.x, stroke[i]!.y);
      }
      ctx.stroke();
    }
  };

  const toSvg = (): string | null => {
    if (!strokes.current.some((s) => s.length > 1)) return null;
    const canvas = canvasRef.current;
    const w = canvas?.getBoundingClientRect().width ?? 320;
    const paths = strokes.current
      .filter((s) => s.length > 1)
      .map((s) => {
        const d = s
          .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
          .join(" ");
        return `<path d="${d}" fill="none" stroke="#1b3a4b" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>`;
      })
      .join("");
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w.toFixed(0)} ${height}" width="${w.toFixed(0)}" height="${height}">${paths}</svg>`;
  };

  const emit = () => {
    const svg = toSvg();
    setHasInk(Boolean(svg));
    onChange(svg);
  };

  useEffect(() => {
    syncSize();
    const onResize = () => syncSize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  return (
    <div className="esign-pad">
      <canvas
        ref={canvasRef}
        className="esign-pad-canvas"
        style={{ height }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          strokes.current.push([pos(e)]);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const cur = strokes.current[strokes.current.length - 1];
          cur?.push(pos(e));
          redraw();
        }}
        onPointerUp={() => {
          drawing.current = false;
          emit();
        }}
        onPointerCancel={() => {
          drawing.current = false;
          emit();
        }}
      />
      <div className="esign-pad-actions">
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            strokes.current = [];
            redraw();
            setHasInk(false);
            onChange(null);
          }}
        >
          پاک کردن
        </button>
        <span className="signing-muted small">
          {hasInk ? "امضا آماده ذخیره است" : "با انگشت یا ماوس امضا بکشید"}
        </span>
      </div>
    </div>
  );
}
